import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useScrolled from '../../hooks/useScrolled';
import useTheme from '../../hooks/useTheme';

function getAppUrl(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:5174';
  if (host.includes('dev.esgtriage.com')) return 'https://actula.dev.esgtriage.com';
  return 'https://actula.dev.esgtriage.com';
}

function SunIcon(): React.ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon(): React.ReactElement {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

export default function Navbar(): React.ReactElement {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { isDark, toggle } = useTheme();

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent): void {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [menuOpen]);

  return (
    <nav
      ref={navRef}
      className={`main-nav${scrolled ? ' nav--scrolled' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container">
        <div className="nav-inner">

          <Link to="/" className="nav-logo" aria-label="ESGTriage home">
            <img src="/images/esgtriage-logo.svg" alt="ESGTriage" className="nav-logo-img" />
          </Link>

          <div className="nav-links">
            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                Platform <span className="nav-dropdown-arrow">&#9660;</span>
              </button>
              <div className="nav-dropdown-menu">
                <Link to="/platform" className="nav-dropdown-product-link">
                  <img src="/images/actula-logo.png" alt="" className="nav-dropdown-product-logo" aria-hidden="true" />
                  Actula Platform
                </Link>
                <Link to="/platform#esg-data">ESG Data &amp; Reporting</Link>
                <Link to="/platform#cbam">CBAM Compliance</Link>
                <Link to="/platform#pcf">Product Carbon Footprint</Link>
                <Link to="/platform#ccf">Corporate Carbon Footprint</Link>
                <Link to="/platform#procurement">Sustainable Procurement</Link>
              </div>
            </div>

            <div className="nav-dropdown">
              <button className="nav-dropdown-trigger">
                Solutions <span className="nav-dropdown-arrow">&#9660;</span>
              </button>
              <div className="nav-dropdown-menu">
                <Link to="/solutions/india">India &mdash; BRSR</Link>
                <Link to="/solutions/europe">Europe &mdash; CSRD / CBAM</Link>
              </div>
            </div>

            <Link to="/about">About</Link>
            <Link to="/resources">Resources</Link>
            <Link to="/contact">Contact</Link>
          </div>

          <div className="nav-ctas">
            <button
              className="theme-toggle"
              onClick={toggle}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>
            <a
              href={getAppUrl()}
              className="btn btn-ghost-dark"
              target="_blank"
              rel="noopener noreferrer"
            >
              Login
            </a>
            <Link to="/contact" className="btn btn-primary">Book a demo</Link>
          </div>

          <button
            className={`hamburger${menuOpen ? ' active' : ''}`}
            aria-label="Toggle mobile menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen(o => !o)}
          >
            <span /><span /><span />
          </button>
        </div>

        <div id="mobile-menu" className={`mobile-menu${menuOpen ? ' open' : ''}`}>
          <Link to="/platform">Platform &mdash; Actula</Link>
          <Link to="/solutions/india">Solutions &mdash; India</Link>
          <Link to="/solutions/europe">Solutions &mdash; Europe</Link>
          <Link to="/about">About</Link>
          <Link to="/resources">Resources</Link>
          <Link to="/contact">Contact</Link>
          <div className="mobile-menu-bottom">
            <button
              className="theme-toggle theme-toggle--mobile"
              onClick={toggle}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
            <Link to="/contact" className="btn btn-primary">Book a demo</Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
